import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { ShoppingBag, DollarSign, UtensilsCrossed, Clock, TrendingUp, BarChart3, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ComposedChart, Line
} from 'recharts';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const date = new Date(label);
  const formatted = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-3 shadow-xl">
      <p className="text-gray-400 text-xs mb-2">{formatted}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <div className="w-2 h-2 rounded-full" style={{ background: entry.color }} />
          <span className="text-gray-300">{entry.name}:</span>
          <span className="text-white font-semibold">
            {entry.name === 'Revenue' ? `₦${entry.value?.toLocaleString()}` : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, salesRes] = await Promise.all([
          axios.get(`${API}/analytics/dashboard`, { headers }),
          axios.get(`${API}/analytics/sales?period=${period}`, { headers })
        ]);
        setStats(statsRes.data);
        setSalesData(salesRes.data.daily_sales || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token, period]);

  // WebSocket for real-time dashboard refresh
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    if (!wsUrl || !token) return;
    let mounted = true;
    const ws = new WebSocket(`${wsUrl}/api/ws`);
    const pingInterval = setInterval(() => {
      if (ws?.readyState === WebSocket.OPEN) try { ws.send('ping'); } catch (_) {}
    }, 15000);

    ws.onmessage = (event) => {
      if (!mounted || event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_order' && mounted) {
          const headers = { Authorization: `Bearer ${token}` };
          Promise.all([
            axios.get(`${API}/analytics/dashboard`, { headers }),
            axios.get(`${API}/analytics/sales?period=${period}`, { headers })
          ]).then(([statsRes, salesRes]) => {
            if (mounted) {
              setStats(statsRes.data);
              setSalesData(salesRes.data.daily_sales || []);
            }
          }).catch(() => {});
        }
      } catch (_) {}
    };

    return () => { mounted = false; clearInterval(pingInterval); ws.close(); };
  }, [token, period]);

  if (loading) {
    return (
      <AdminLayout title="Dashboard" subtitle="Welcome back! Here's what's happening today.">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    { title: "Today's Orders", value: stats?.today_orders || 0, subtitle: `${stats?.total_orders || 0} total`, icon: ShoppingBag, color: "bg-blue-500/20 text-blue-400" },
    { title: "Today's Revenue", value: `₦${(stats?.today_revenue || 0).toLocaleString()}`, subtitle: `₦${(stats?.total_revenue || 0).toLocaleString()} total`, icon: DollarSign, color: "bg-green-500/20 text-green-400" },
    { title: "Active Menu Items", value: stats?.active_menu_items || 0, subtitle: "Available now", icon: UtensilsCrossed, color: "bg-purple-500/20 text-purple-400" },
    { title: "Pending Orders", value: stats?.pending_orders || 0, subtitle: "Need attention", icon: Clock, color: "bg-fire-500/20 text-fire-500" },
  ];

  // Compute quick metrics from sales data
  const totalPeriodRevenue = salesData.reduce((sum, d) => sum + (d.revenue || 0), 0);
  const totalPeriodOrders = salesData.reduce((sum, d) => sum + (d.orders || 0), 0);
  const avgOrderValue = totalPeriodOrders > 0 ? Math.round(totalPeriodRevenue / totalPeriodOrders) : 0;

  return (
    <AdminLayout title="Dashboard" subtitle="Welcome back! Here's what's happening today.">
      <div data-testid="admin-dashboard">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="bg-card border-white/10" data-testid={`stat-card-${index}`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="text-gray-400 text-xs sm:text-sm mb-1 truncate">{stat.title}</p>
                      <p className="text-white text-xl sm:text-3xl font-bold">{stat.value}</p>
                      <p className="text-gray-500 text-xs mt-1">{stat.subtitle}</p>
                    </div>
                    <div className={`p-2 sm:p-3 rounded-xl ${stat.color} flex-shrink-0`}>
                      <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Revenue + Orders Chart */}
        <Card className="bg-card border-white/10 mb-8" data-testid="sales-chart">
          <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-white">Revenue & Orders</CardTitle>
              <p className="text-gray-500 text-sm mt-1">
                ₦{totalPeriodRevenue.toLocaleString()} revenue from {totalPeriodOrders} orders
                {avgOrderValue > 0 && ` (avg ₦${avgOrderValue.toLocaleString()})`}
              </p>
            </div>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 bg-black/50 border-white/10 text-white" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="h-72 sm:h-80">
              {salesData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={salesData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF4500" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FF4500" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                    <XAxis
                      dataKey="_id"
                      stroke="#555"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { weekday: 'short' })}
                    />
                    <YAxis
                      yAxisId="revenue"
                      stroke="#555"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      yAxisId="orders"
                      orientation="right"
                      stroke="#555"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{ paddingTop: 8 }}
                      formatter={(value) => <span className="text-gray-400 text-xs">{value}</span>}
                    />
                    <Area
                      yAxisId="revenue"
                      type="monotone"
                      dataKey="revenue"
                      name="Revenue"
                      stroke="#FF4500"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorRevenue)"
                    />
                    <Bar
                      yAxisId="orders"
                      dataKey="orders"
                      name="Orders"
                      fill="#3B82F6"
                      fillOpacity={0.6}
                      radius={[4, 4, 0, 0]}
                      barSize={24}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <BarChart3 className="w-12 h-12 mb-3 opacity-30" />
                  <p>No sales data yet</p>
                  <p className="text-xs mt-1">Data will appear as orders come in</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bottom row: Top Items + Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Selling Items */}
          <Card className="bg-card border-white/10" data-testid="top-items-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-fire-500" />
                Top Selling Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(stats?.top_items || []).map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-fire-500/20 flex items-center justify-center text-fire-500 font-bold text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{item.name}</p>
                      <p className="text-gray-500 text-xs">{item.count} sold</p>
                    </div>
                    <div className="text-fire-500 font-bold text-sm">₦{(item.revenue || 0).toLocaleString()}</div>
                  </div>
                ))}
                {(!stats?.top_items || stats.top_items.length === 0) && (
                  <p className="text-gray-500 text-center py-4 text-sm">No sales data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Daily Order Count Mini Chart */}
          <Card className="bg-card border-white/10" data-testid="order-trend-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                Daily Order Count
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                {salesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={salesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                      <XAxis
                        dataKey="_id"
                        stroke="#555"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                      />
                      <YAxis stroke="#555" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value) => [value, 'Orders']}
                        labelFormatter={(v) => new Date(v).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                      />
                      <Bar dataKey="orders" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                    No order data yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
